import * as Oidc from 'oidc-client';
import { getUrlFromCurrentBase } from 'common/format-parse';
import { Roles, TeamRoutes} from 'typings/enums';
import { userDataManager } from 'singletons';

interface ISignoutResponse {

}

export class OpenIdAuthentication {

    public static redirectUri: string = getUrlFromCurrentBase('signin-oidc');
    private userManager: Oidc.UserManager;
    private readonly stateUrlKey: string = 'stateUrl';

    setupOpenId(authority: string, clientId: string): void {
        this.userManager = new Oidc.UserManager({
            authority,
            client_id: clientId,
            redirect_uri: OpenIdAuthentication.redirectUri,
            post_logout_redirect_uri: getUrlFromCurrentBase(''),
            stateStore: new Oidc.WebStorageStateStore({
                store: window.sessionStorage
            }),
            response_type: 'id_token token',
            filterProtocolClaims: true,
            loadUserInfo: false,
            scope: 'openid profile payscale email teampay employee notification conversation'
        });
    }

    async signIn(redirectUrl: string): Promise<Nullable<Oidc.User>> {
        const user = await this.userManager.getUser();
        if (!user) {
            await this.setStateUrl(redirectUrl);
            await this.userManager.signinRedirect();
            return null;
        }

        return user;
    }

    async authorizePageAccess(userRoles: string[], pageUrl: string, masterAccountId?: string): Promise<boolean> {
        if (pageUrl !== TeamRoutes.Support && masterAccountId == null) {
            this.redirectToSupportPage();
            return false;
        }

        if (pageUrl !== TeamRoutes.AccessDenied && pageUrl !== TeamRoutes.Support && masterAccountId == null) {
            this.redirectOnUnauthorizedPage();
            return false;
        }

        if (pageUrl === TeamRoutes.CycleConfiguration &&
            userRoles.find(role => role === Roles.HumanResource || role === Roles.Support) === undefined) {
            this.redirectOnUnauthorizedPage();
            return false;
        }

        if (pageUrl === TeamRoutes.GlobalConfiguration &&
            userRoles.find(role => role === Roles.HumanResource || role === Roles.Support) === undefined) {
            this.redirectOnUnauthorizedPage();
            return false;
        }

        if (pageUrl === TeamRoutes.SummaryDashboard &&
            userRoles.find(role =>
                role === Roles.HumanResource
                    || role === Roles.ManagerOfManager
                    || role === Roles.Support
                    || role === Roles.Hrbp) === undefined) {
            this.redirectOnUnauthorizedPage();
            return false;
        }

        return true;
    }

    async signOut(): Promise<ISignoutResponse> {
        try {
            await userDataManager.clearUserInfo();
        } catch {
            // This can fail if the userInfo was never loaded so don't do anything
            // if it fails and let the user still logout
        }

        return this.userManager.signoutRedirect() as ISignoutResponse;
    }

    handleSignInCallback(): Promise<Oidc.User> {
        return this.userManager.signinRedirectCallback();
    }

    setupLogoutOtherTab(): void {
        this.userManager.events.addUserSignedOut(async () => {
            await this.handleUnauthorizedCall(OpenIdAuthentication.redirectUri);
        });
    }

    async handleUnauthorizedCall(redirectUrl: string): Promise<Nullable<Oidc.User>> {
        await Promise.all([
            this.userManager.removeUser(),
            this.userManager.revokeAccessToken()
        ]);

        await this.userManager.clearStaleState();
        return await this.signIn(redirectUrl);
    }

    async getStateUrl(): Promise<string> {
        const stateStore = this.userManager.settings.stateStore;
        return await stateStore?.get(this.stateUrlKey) ?? undefined;
    }

    getUser(): Promise<Oidc.User|null> {
        return this.userManager.getUser();
    }

    private async setStateUrl(url: string): Promise<void> {
        const stateStore = this.userManager.settings.stateStore;
        await stateStore?.set(this.stateUrlKey, url);
    }

    private redirectOnUnauthorizedPage(): void {
        window.location.assign(TeamRoutes.AccessDenied);
    }

    private redirectToSupportPage(): void {
        window.location.assign(TeamRoutes.Support);
    }
}

const openIdAuthentication = new OpenIdAuthentication();

export default openIdAuthentication;